import React, { useState, useEffect } from "react";
import TextInput from "../../../../../../v2/components/form/text-input";
import Select from "../../../../../../v2/components/form/select";
import Button from "../../../../../../v2/components/new-button";
import Collapsible from "../../../../../../v2/components/collapsible";
import styles from "./styles.module.scss";
import Modal from "../../../../../components/modal";
import { DROPDOWN_FIELDS, TEXTAREA_FIELDS } from "./constants";
import {
  formatMetadata,
} from "../metadata";
import { getCountries } from "../../../../../../api/index";

const EditSubmissionDataModal = ({
  isOpen,
  onClose,
  powerapp,
  onSave
}) => {
  const [editableData, setEditableData] = useState({});
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    getCountries().then((res) => {
      setCountries(res.data);
    });
  }, []);

  const dropdownFields = {
    ...DROPDOWN_FIELDS,
    DocumentCountry: {
      options: countries,
    },
  };

  // Shared utility functions
  const utils = {
    removeCountryCode: (phoneNumber) => {
      phoneNumber = phoneNumber.replace(/[^0-9]/g, "");
      if (phoneNumber.length > 10 && phoneNumber.charAt(0) === "1") {
        phoneNumber = phoneNumber.substring(1);
        console.log("🔧 phoneNumber:", phoneNumber);
      }
      return phoneNumber;
    },
    getStateInitial: (stateName) => {
      // This would need to be implemented based on your state mapping
      return stateName?.substring(0, 2).toUpperCase() || "";
    },
    normalizeOwnershipType: (ownership) => {
      // Normalize ownership values to match dropdown options
      const ownershipMap = {
        'sole proprietor': 'sole',
        'sole': 'sole',
        'partnership': 'partnership',
        'corporation': 'corporation',
        'llc': 'llc',
        'limited': 'llc',
        'limited liability company': 'llc',
        'limited liability': 'llc'

      };

      if (!ownership) return 'sole';

      const normalized = ownershipMap[ownership.toLowerCase()];
      return normalized || 'sole';
    }
  };

  // Format powerapp metadata
  const formatAppMetadata = (powerapp) => {
    if (!powerapp) return {};

    const formattedData = formatMetadata(powerapp, utils);

    // Special handling for appPdfBundle - ensure it's a JSON string
    if (formattedData.appPdfBundle) {
      formattedData.appPdfBundle = JSON.stringify(formattedData.appPdfBundle, null, 2);
    }

    return formattedData;
  };

  useEffect(() => {
    if (isOpen && powerapp) {
      setDataLoading(true);
      const formattedData = formatAppMetadata(powerapp);
      setEditableData(formattedData);
      setDataLoading(false);
    }
  }, [isOpen, powerapp]);

  const handleSave = async () => {
    setLoading(true);
    setValidationErrors({}); // Clear previous errors

    try {
      // Prepare data for saving
      const dataToSave = { ...editableData };

      // Special handling for appPdfBundle - parse JSON string to object
      if (dataToSave.appPdfBundle && typeof dataToSave.appPdfBundle === 'string') {
        try {
          dataToSave.appPdfBundle = JSON.parse(dataToSave.appPdfBundle);
        } catch (e) {
          setValidationErrors({
            'general_error': ['Invalid JSON format in appPdfBundle']
          });
          setLoading(false);
          return;
        }
      }

      await onSave(dataToSave);
      // Only close modal if save was successful (no errors thrown)
      onClose();
    } catch (error) {
      console.error("Error saving data:", error);
      // Handle validation errors (422) - match the pattern used in other components
      if (error.status === 422 && error.data?.errors) {
        console.log("Setting validation errors:", error.data.errors);
        setValidationErrors(error.data.errors);
        // Don't close modal - let user see and fix the errors
      } else if (error.status == 500) {
        // Fallback for different error structure
        console.log(
          "Setting validation errors (response):",
          error?.data?.message,
        );
        const errorMessage = error?.data?.message || "Error saving data";
        setValidationErrors({
          'general_error': [errorMessage]
        });
      } else {
        // Handle other errors
        const errorMessage = error?.data?.message || "Error saving data";
        setValidationErrors({
          'general_error': [errorMessage]
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (path, value) => {
    console.log("🔧 handleFieldChange called with:", { path, value });
    const newData = { ...editableData };
    const keys = path.split(".");
    let current = newData;

    // Navigate to the parent object (before the last key)
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];

      if (key.includes("[")) {
        const arrayKey = key.split("[")[0];
        // Fix: Extract index properly by removing the closing bracket
        const indexStr = key.split("[")[1].replace("]", "");
        const index = parseInt(indexStr);

        // Ensure the array exists
        if (!current[arrayKey]) {
          current[arrayKey] = [];
        }
        // Ensure the array element exists
        if (!current[arrayKey][index]) {
          current[arrayKey][index] = {};
        }
        current = current[arrayKey][index];
      } else {
        // Ensure the object exists
        if (!current[key]) {
          current[key] = {};
        }
        current = current[key];
      }
    }

    // Set the value on the last key
    const lastKey = keys[keys.length - 1];

    if (lastKey.includes("[")) {
      const arrayKey = lastKey.split("[")[0];
      // Fix: Extract index properly by removing the closing bracket
      const indexStr = lastKey.split("[")[1].replace("]", "");
      const index = parseInt(indexStr);

      if (!current[arrayKey]) {
        current[arrayKey] = [];
      }
      current[arrayKey][index] = value;
    } else {
      current[lastKey] = value;
    }

    setEditableData(newData);
  };

  const renderDropdownField = (key, value, path) => {
    const dropdownConfig = dropdownFields[key];
    if (!dropdownConfig) return null;

    // Try exact match first, then case-insensitive match
    let selectedOption = dropdownConfig.options.find(option =>
      option.value === value || option.value === String(value)
    );

    // If no exact match, try case-insensitive match
    if (!selectedOption && value) {
      selectedOption = dropdownConfig.options.find(option =>
        option.value.toLowerCase() === String(value).toLowerCase()
      );
    }

    const searchableFields = ['DocumentState', 'DocumentCountry'];

    return (
      <div>
        <Select
          label={key}
          placeholder={`Select ${key}`}
          value={selectedOption ? selectedOption.value : ""}
          onChange={(value) => handleFieldChange(path, value)}
          options={dropdownConfig.options}
          placeholderAsBlankOption={true}
          search={searchableFields.includes(key)}
        />
      </div>
    );
  };

  const renderTextareaField = (key, value, path) => {
    if (!TEXTAREA_FIELDS.includes(key)) return null;

    // Special handling for appPdfBundle - ensure it's displayed as JSON string
    let displayValue = value;
    if (key === 'appPdfBundle') {
      if (typeof value === 'string') {
        try {
          // If it's a JSON string, format it nicely
          const parsed = JSON.parse(value);
          displayValue = JSON.stringify(parsed, null, 2);
        } catch (e) {
          // If it's not valid JSON, display as is
          displayValue = value;
        }
      }
    }

    return (
      <div>
        <label
          className={styles.editSubmissionModaTextArealLabel}
          htmlFor={key}
        >
          {key}
        </label>
        <textarea
          name={key}
          value={displayValue || ""}
          className={styles.editSubmissionModalTextArea}
          onChange={(e) => handleFieldChange(path, e.target.value)}
          placeholder={key}
          rows={key === 'appPdfBundle' ? 15 : 4} // More rows for JSON
        />
      </div>
    );
  };

  const handleDeleteOwner = (index) => {
    const newData = { ...editableData };
    newData.Owners.splice(index, 1);
    setEditableData(newData);
  };

  /**
   * Determines if a document-related field should be rendered based on DocumentType.
   * - PASSPORT: show DocumentCountry, hide DocumentState
   * - STATE_ID / LOCAL_STATE_ID: show DocumentState, hide DocumentCountry
   */
  const shouldRenderDocumentField = (fieldKey, ownerItem) => {
    const documentType = ownerItem?.DocumentType;

    if (fieldKey === "DocumentCountry") {
      return documentType === "PASSPORT";
    }

    if (fieldKey === "DocumentState") {
      return documentType !== "PASSPORT";
    }

    return true;
  };

  const renderEditableField = (key, value, path = "", isRoot = false) => {
    const fullPath = path ? `${path}.${key}` : key;

    if (key === "appPdfBundle") {
      return renderTextareaField(key, value, fullPath);
    }

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      return (
        <div key={fullPath} className={styles.colSpan2}>
          <Collapsible
            title={key}
            headingUppercase={false}
            defaultExpanded={isRoot}
          >
            <div
              className={`${styles.grid} ${styles.gridCols2} ${styles.gap2}`}
            >
              {Object.entries(value).map(([subKey, subValue]) => (
                <React.Fragment key={`${fullPath}.${subKey}`}>
                  {renderEditableField(subKey, subValue, fullPath)}
                </React.Fragment>
              ))}
            </div>
          </Collapsible>
        </div>
      );
    }

    if (Array.isArray(value)) {
      return (
        <div key={fullPath} className={styles.colSpan2}>

          {value.map((item, index) => (
            <Collapsible
              key={`${fullPath}[${index}]`}
              title={`${key} ${index + 1}`}
              headingUppercase={false}
              defaultExpanded={isRoot}
              className={styles.mb1}
            >
              <div
                className={`${styles.grid} ${styles.gridCols2} ${styles.gap2}`}
              >

                <React.Fragment key={`${fullPath}[${index}]`}>
                  {Object.entries(item)
                    .filter(([subKey]) => shouldRenderDocumentField(subKey, item))
                    .map(([subKey, subValue]) => (
                      <React.Fragment key={`${fullPath}[${index}].${subKey}`}>
                        {renderEditableField(
                          subKey,
                          subValue,
                          `${fullPath}[${index}]`,
                        )}
                      </React.Fragment>
                    ))}
                </React.Fragment>
              </div>

              {key === 'Owners' && index > 0 && (
                <Button
                  variant="ghost"
                  onClick={() => handleDeleteOwner(index)}
                >Delete</Button>
              )}

            </Collapsible>
          ))}

        </div>
      );
    }

    // Base case: render individual field (text input or dropdown)
    if (dropdownFields[key]) {
      return renderDropdownField(key, value, fullPath);
    }

    if (TEXTAREA_FIELDS.includes(key)) {
      return renderTextareaField(key, value, fullPath);
    }

    return (
      <div key={fullPath}>
        <TextInput
          fullWidth
          label={key}
          value={value || ""}
          onChange={(e) => handleFieldChange(fullPath, e.target.value)}
        />
      </div>
    );
  };

  const formatFieldName = (path) => {
    // Handle special error types
    if (path === 'general_error') {
      return 'Error';
    }

    const keys = path.split(".");
    let formattedPath = "";
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key.includes("[")) {
        const arrayKey = key.split("[")[0];
        const index = parseInt(key.split("[")[1]);
        formattedPath += `${arrayKey}[${index}].`;
      } else {
        formattedPath += `${key}.`;
      }
    }
    return formattedPath.slice(0, -1);
  };

  return (
    <Modal
      header="Edit submission data"
      size="md"
      notCentered
      isOpen={isOpen}
      onClose={onClose}
    >
      {!powerapp ? (
        <div className={styles.editSubmissionModalHeader}>
          <h3>Loading...</h3>
        </div>
      ) : (
        <>
          {dataLoading ? (
            <div style={{ textAlign: "center", padding: "2rem" }}>
              <h5>Loading submission data...</h5>
            </div>
          ) : (
            <div className={styles.editSubmissionModalContent}>
              <div
                className={`${styles.grid} ${styles.gridCols2} ${styles.gap2}`}
              >
                {Object.entries(editableData).map(([key, value]) => {
                  if (typeof value === "object" && value !== null) {
                    return renderEditableField(key, value, "", true);
                  }
                  return renderEditableField(key, value);
                })}
              </div>
            </div>
          )}
        </>
      )}

      <div className={styles.editSubmissionModalFooter}>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button type="button" onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </Button>
      </div>

      {/* Validation Errors Section */}
      {Object.keys(validationErrors).length > 0 && (
        <div className={styles.editSubmissionModalErrors}>
          <div className={styles.errorTitle}>
            Please fix the following errors:
          </div>
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {Object.entries(validationErrors).map(([field, errors]) => (
              <div key={field} className={styles.errorField}>
                <div className={styles.fieldName}>
                  {formatFieldName(field)}:
                </div>
                {Array.isArray(errors) ? (
                  errors.map((error, index) => (
                    <div key={index} className={styles.errorMessage}>
                      • {error}
                    </div>
                  ))
                ) : (
                  <div className={styles.errorMessage}>• {errors}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EditSubmissionDataModal;
